import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import { useTranslation } from 'react-i18next';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { TextInput } from '../../../../../../../ui/input/text_input/text_input';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { PreQualQuestionType } from '../interfaces';

interface CreateQuestionCardProps {
  newQuestionText: string;
  setNewQuestionText: (text: string) => void;
  newQuestionType: PreQualQuestionType;
  setNewQuestionType: (type: PreQualQuestionType) => void;
  questionTypeOptions: SelectOptions[];
  choices: string[];
  setChoices: React.Dispatch<React.SetStateAction<string[]>>;
  onChoiceChange: (index: number, value: string, edit: boolean) => void;
  onAddQuestion: () => Promise<void>;
  isEditingOther: boolean;
}

export const CreateQuestionCard = ({
  newQuestionText,
  setNewQuestionText,
  newQuestionType,
  setNewQuestionType,
  questionTypeOptions,
  choices,
  setChoices,
  onChoiceChange,
  onAddQuestion,
  isEditingOther,
}: CreateQuestionCardProps) => {
  const { t } = useTranslation(NS.adminPages);

  const isSaveDisabled = isEditingOther || newQuestionText.trim() === '';

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: '14px',
        bgcolor: 'slate.50',
        border: '1px solid',
        borderColor: 'slate.200',
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
      }}
    >
      {/* Card Header Tag */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 4,
            height: 16,
            borderRadius: '2px',
            bgcolor: 'success.main',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: 'text.secondary',
            fontSize: '0.7rem',
          }}
        >
          {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.createNew)}
        </Typography>
      </Box>

      {/* Inputs Row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '7fr 3fr 2fr' },
          gap: 2,
          alignItems: 'end',
        }}
      >
        <Box>
          <TextInput
            label={t(
              ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionText,
            )}
            name="question"
            value={newQuestionText}
            type="text"
            placeholder={t(
              ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.questionPlaceholder,
            )}
            onChange={(e) => setNewQuestionText(e.target.value)}
            disabled={isEditingOther}
          />
        </Box>

        <Box>
          <CustomSelect
            label={t(
              ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.responseType,
            )}
            name="questionType"
            value={questionTypeOptions.find(
              (option) => option.value === newQuestionType,
            )}
            options={questionTypeOptions}
            onChange={(selectedOption: SelectOptions) =>
              setNewQuestionType(selectedOption.value as PreQualQuestionType)
            }
            disabled={isEditingOther}
          />
        </Box>

        <Box sx={{ display: 'flex', height: 42 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={onAddQuestion}
            disabled={isSaveDisabled}
            startIcon={<AddCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{
              height: 42,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              },
            }}
          >
            {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.saveQuestion)}
          </Button>
        </Box>
      </Box>

      {/* Multiple Choice Options Builder */}
      {newQuestionType === 'multiple-choice' && (
        <Box
          sx={{
            p: 2,
            borderRadius: '10px',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              fontSize: '0.675rem',
            }}
          >
            {t(
              ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.optionsChoices,
            )}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            {choices.map((choice, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
              >
                <Box sx={{ width: 190 }}>
                  <TextInput
                    name={`choice-${index}`}
                    value={choice}
                    type="text"
                    placeholder={t(
                      ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.choicePlaceholder,
                      { index: index + 1 },
                    )}
                    sx={{ height: 36 }}
                    onChange={(e) =>
                      onChoiceChange(index, e.target.value, false)
                    }
                  />
                </Box>
                {choices.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() =>
                      setChoices(choices.filter((_, i) => i !== index))
                    }
                    sx={{
                      color: 'text.secondary',
                      '&:hover': { color: 'error.main', bgcolor: 'error.50' },
                    }}
                  >
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
            ))}

            <Button
              size="small"
              variant="outlined"
              onClick={() => setChoices([...choices, ''])}
              startIcon={<AddRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                height: 36,
                borderRadius: '8px',
                borderStyle: 'dashed',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            >
              {t(ADMIN_PAGES_KEYS.jobOpenings.jobPost.preQualifying.addChoice)}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
